import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTheme } from "next-themes";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import ForcePasswordChangeDialog from "@/components/ForcePasswordChangeDialog";
import ReadOnlyBanner from "@/components/ReadOnlyBanner";
import {
    BookOpenCheck,
    LayoutDashboard,
    PackageSearch,
    LineChart,
    Settings,
    HelpCircle,
    LogOut,
    ChevronDown,
    Sun,
    Moon,
    Building2,
    Check,
    UsersRound,
    Upload,
    Download,
    Wrench,
    Eraser,
    Archive,
    AlertOctagon,
    Menu,
    X,
    PanelLeftClose,
    PanelLeftOpen,
    KeyRound,
    ArrowLeftRight,
} from "lucide-react";

const NAV_SECTIONS = [
    {
        label: "Arbeitsbereich",
        items: [
            { to: "uebersicht", label: "Übersicht", icon: LayoutDashboard, testid: "nav-uebersicht" },
            { to: "inventar", label: "Inventar", icon: PackageSearch, testid: "nav-inventar" },
            { to: "berichte", label: "Berichte", icon: LineChart, testid: "nav-berichte" },
            { to: "rollforward", label: "Roll-Forward", icon: ArrowLeftRight, testid: "nav-rollforward" },
        ],
    },
    {
        label: "Daten",
        items: [
            { to: "import", label: "Import", icon: Upload, testid: "nav-import", roles: ["admin", "user"] },
            { to: "hk-pflege", label: "HK-Pflege", icon: Wrench, testid: "nav-hk-pflege", roles: ["admin", "user"] },
            { to: "vorschau-pflege", label: "Vorschau-Pflege", icon: Eraser, testid: "nav-vorschau-pflege", roles: ["admin"] },
            { to: "snapshots", label: "Snapshots / Abschluss", icon: Archive, testid: "nav-snapshots", roles: ["admin", "user"] },
            { to: "sonder-wb", label: "Sonder-WB", icon: AlertOctagon, testid: "nav-sonder-wb", roles: ["admin"] },
            { to: "export", label: "Export", icon: Download, testid: "nav-export" },
        ],
    },
    {
        label: "Verwaltung",
        items: [
            { to: "benutzer", label: "Benutzer", icon: UsersRound, testid: "nav-benutzer", roles: ["admin"] },
            { to: "einstellungen", label: "Einstellungen", icon: Settings, testid: "nav-einstellungen", roles: ["admin"] },
        ],
    },
    {
        label: "Support",
        items: [{ to: "hilfe", label: "Hilfe", icon: HelpCircle, testid: "nav-hilfe" }],
    },
];

function NavItem({ item, collapsed, onNav }) {
    const linkClass = ({ isActive }) => {
        const base =
            "appearance-none outline-none focus:outline-none focus-visible:outline-none select-none no-underline";
        if (collapsed) {
            // No background container, no border — only icon color changes.
            return `${base} flex items-center justify-center h-10 w-10 mx-auto text-sm transition-colors ${
                isActive
                    ? "text-tenant"
                    : "text-muted-foreground hover:text-foreground"
            }`;
        }
        return `${base} flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
            isActive
                ? "bg-tenant-soft text-tenant border-l-2 border-tenant"
                : "text-muted-foreground hover:text-foreground hover:bg-surface2"
        }`;
    };
    const link = (
        <NavLink to={`/${item.to}`} onClick={onNav} data-testid={item.testid} className={linkClass}>
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span>{item.label}</span>}
        </NavLink>
    );
    if (collapsed) {
        return (
            <TooltipProvider delayDuration={150}>
                <Tooltip>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right" className="text-xs">
                        {item.label}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }
    return link;
}

function SidebarContent({ activeTenant, role, onNav, collapsed, onLogoClick }) {
    return (
        <>
            <button
                type="button"
                onClick={onLogoClick}
                data-testid="sidebar-logo-btn"
                title="Zur Verlagsauswahl"
                className={`${collapsed ? "px-4" : "px-5"} py-5 w-full flex items-center gap-2.5 border-b border-border hover:bg-surface2/60 transition text-left`}
            >
                <div className="h-9 w-9 rounded-md flex items-center justify-center bg-tenant text-[color:var(--tenant-accent-foreground)] shrink-0">
                    <BookOpenCheck className="h-4 w-4" />
                </div>
                {!collapsed && (
                    <div>
                        <div className="font-heading font-semibold tracking-tight text-sm">INVENTUR PRO</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            Inventurbewertung
                        </div>
                    </div>
                )}
            </button>

            {activeTenant && !collapsed && (
                <div className="px-5 py-4 border-b border-border">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                        Aktiver Verlag
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: activeTenant.color }} />
                        <div className="leading-tight">
                            <div className="text-sm font-medium">{activeTenant.name}</div>
                            <div className="text-xs text-muted-foreground">{activeTenant.city}</div>
                        </div>
                    </div>
                </div>
            )}

            {activeTenant && collapsed && (
                <div className="px-4 py-3 border-b border-border flex justify-center">
                    <TooltipProvider delayDuration={150}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span
                                    className="h-2.5 w-2.5 rounded-full"
                                    style={{ background: activeTenant.color }}
                                />
                            </TooltipTrigger>
                            <TooltipContent side="right" className="text-xs">
                                {activeTenant.name} · {activeTenant.city}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            )}

            <nav
                className={`flex-1 overflow-y-auto ${collapsed ? "px-3 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" : "p-3 space-y-5"}`}
            >
                {collapsed
                    ? // Flat list of all icons – no section grouping, uniform spacing
                      NAV_SECTIONS.flatMap((section) =>
                          section.items.filter((i) => !i.roles || i.roles.includes(role)),
                      ).map((item, i) => (
                          <div key={item.to} className={i === 0 ? "" : "mt-1"}>
                              <NavItem item={item} collapsed onNav={onNav} />
                          </div>
                      ))
                    : NAV_SECTIONS.map((section) => {
                          const items = section.items.filter((i) => !i.roles || i.roles.includes(role));
                          if (items.length === 0) return null;
                          return (
                              <div key={section.label}>
                                  <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                      {section.label}
                                  </div>
                                  <div className="space-y-0.5">
                                      {items.map((item) => (
                                          <NavItem key={item.to} item={item} collapsed={false} onNav={onNav} />
                                      ))}
                                  </div>
                              </div>
                          );
                      })}
            </nav>
        </>
    );
}

export default function AppShell() {
    const { user, logout, pendingLoginPassword } = useAuth();
    const { activeTenant, tenants, setActiveTenant, clearTenant } = useTenant();
    const { theme, setTheme } = useTheme();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem("inventur_sidebar_collapsed") === "1");
    const [pwDialogOpen, setPwDialogOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem("inventur_sidebar_collapsed", collapsed ? "1" : "0");
    }, [collapsed]);

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    const handlePwChanged = async () => {
        // Sessions wurden serverseitig invalidiert → sauber ausloggen
        await logout();
        navigate("/login", { replace: true });
    };

    const goToTenantSelection = () => {
        // Aktiver Verlag wird zurückgesetzt, damit die Auswahl neutral ist
        clearTenant?.();
        navigate("/verlag-waehlen");
    };

    const sidebarW = collapsed ? "w-20" : "w-64";

    return (
        <div className="min-h-screen bg-background text-foreground flex">
            {/* Desktop / Notebook Sidebar (ab 768px) */}
            <aside
                className={`hidden md:flex ${sidebarW} shrink-0 border-r border-border bg-surface1 flex-col transition-[width] duration-200 ease-out relative`}
                data-testid="desktop-sidebar"
                data-collapsed={collapsed}
            >
                <SidebarContent activeTenant={activeTenant} role={user?.role} collapsed={collapsed} onLogoClick={goToTenantSelection} />
                <div className={`border-t border-border ${collapsed ? "p-2" : "p-3"} space-y-1`}>
                    {collapsed ? (
                        <TooltipProvider delayDuration={150}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={handleLogout}
                                        data-testid="sidebar-logout-btn"
                                        className="appearance-none outline-none focus:outline-none flex items-center justify-center h-10 w-10 mx-auto text-sm text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <LogOut className="h-4 w-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="text-xs">
                                    Abmelden
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ) : (
                        <button
                            onClick={handleLogout}
                            data-testid="sidebar-logout-btn"
                            className="flex items-center gap-2 w-full px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-surface2 transition-colors"
                        >
                            <LogOut className="h-4 w-4" /> Abmelden
                        </button>
                    )}
                </div>
                {/* Toggle handle */}
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    data-testid="sidebar-toggle"
                    title={collapsed ? "Menü ausklappen" : "Menü einklappen"}
                    className="absolute -right-3 top-20 h-7 w-7 rounded-full bg-surface1 border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-tenant shadow-sm transition-colors z-10"
                >
                    {collapsed ? <PanelLeftOpen className="h-3.5 w-3.5" /> : <PanelLeftClose className="h-3.5 w-3.5" />}
                </button>
            </aside>

            {/* Mobile Sidebar Drawer (< 768px) */}
            {mobileOpen && (
                <div className="md:hidden fixed inset-0 z-50 flex">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
                    <aside className="relative w-72 bg-surface1 border-r border-border flex flex-col">
                        <div className="absolute top-3 right-3">
                            <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                        <SidebarContent
                            activeTenant={activeTenant}
                            role={user?.role}
                            onNav={() => setMobileOpen(false)}
                            collapsed={false}
                            onLogoClick={() => { setMobileOpen(false); goToTenantSelection(); }}
                        />
                        <button
                            onClick={handleLogout}
                            className="m-3 flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-surface2"
                        >
                            <LogOut className="h-4 w-4" /> Abmelden
                        </button>
                    </aside>
                </div>
            )}

            {/* Main */}
            <div className="flex-1 min-w-0 flex flex-col">
                <header className="h-16 border-b border-border bg-surface1/80 backdrop-blur supports-[backdrop-filter]:bg-surface1/60 flex items-center justify-between px-4 md:px-8 sticky top-0 z-30">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="md:hidden"
                            onClick={() => setMobileOpen(true)}
                            data-testid="mobile-menu-btn"
                        >
                            <Menu className="h-5 w-5" />
                        </Button>
                        {/* Publisher quick-switch dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="h-10 gap-2 border-border"
                                    data-testid="tenant-switch-dropdown"
                                >
                                    <Building2 className="h-4 w-4" />
                                    {activeTenant ? (
                                        <>
                                            <span
                                                className="h-2.5 w-2.5 rounded-full"
                                                style={{ background: activeTenant.color }}
                                            />
                                            <span className="font-medium">{activeTenant.name}</span>
                                            <span className="text-muted-foreground text-xs hidden sm:inline">
                                                · {activeTenant.city}
                                            </span>
                                        </>
                                    ) : (
                                        <span className="font-medium text-muted-foreground">Verlag wählen…</span>
                                    )}
                                    <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-72">
                                <DropdownMenuLabel className="text-xs uppercase tracking-widest text-muted-foreground">
                                    Verlag wechseln
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {tenants.map((t) => (
                                    <DropdownMenuItem
                                        key={t.tenant_id}
                                        onClick={() => setActiveTenant(t)}
                                        data-testid={`switch-tenant-${t.tenant_id}`}
                                        className="cursor-pointer py-2.5"
                                    >
                                        <span className="h-2.5 w-2.5 rounded-full mr-2" style={{ background: t.color }} />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium">{t.name}</div>
                                            <div className="text-xs text-muted-foreground">{t.city}</div>
                                        </div>
                                        {activeTenant?.tenant_id === t.tenant_id && (
                                            <Check className="h-4 w-4 text-tenant" />
                                        )}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                            data-testid="theme-toggle-btn"
                            title="Hell/Dunkel umschalten"
                        >
                            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-10 gap-2 px-2" data-testid="user-menu-trigger">
                                    <div className="h-7 w-7 rounded-full bg-tenant text-[color:var(--tenant-accent-foreground)] flex items-center justify-center text-xs font-semibold">
                                        {user?.name?.[0]?.toUpperCase() || "U"}
                                    </div>
                                    <div className="hidden md:block text-left leading-tight">
                                        <div className="text-sm font-medium">{user?.name}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                            {user?.role}
                                        </div>
                                    </div>
                                    <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="text-xs">
                                    <div className="font-medium">{user?.email}</div>
                                    <div className="text-muted-foreground text-[10px] uppercase tracking-widest mt-0.5">
                                        Rolle: {user?.role}
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate("/verlag-waehlen")} data-testid="user-menu-switch">
                                    <Building2 className="h-4 w-4 mr-2" /> Anderen Verlag wählen
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setPwDialogOpen(true)} data-testid="user-menu-change-pw">
                                    <KeyRound className="h-4 w-4 mr-2" /> Passwort ändern
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleLogout} data-testid="user-menu-logout">
                                    <LogOut className="h-4 w-4 mr-2" /> Abmelden
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                <main className="flex-1 px-4 md:px-8 py-8">
                    <div className="mb-4"><ReadOnlyBanner /></div>
                    <Outlet />
                </main>
            </div>
            <ChangePasswordDialog
                open={pwDialogOpen}
                onOpenChange={setPwDialogOpen}
                onSuccess={handlePwChanged}
            />
            <ForcePasswordChangeDialog
                open={user?.must_change_password === true}
                currentPassword={pendingLoginPassword}
            />
        </div>
    );
}
